import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MenuMotion, naturalMenuWidth } from '../menu-motion';
import { BehaviorSubject, of, Subject, timer } from 'rxjs';
import { AliveState } from '../../../core/ActiveState';
import {
  debounceTime,
  filter,
  first,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ZalamoDialogService } from '../../zalamo-dialog/zalamo-dialog.service';
import { FullscreenRef } from '../../zalamo-dialog/fullscreen-ref';
import { select, Store } from '@ngrx/store';
import {
  navigationMobileSubmenuClose,
  navigationSetHasSubmenuState,
} from '../navigation-state/navigation-state.actions';
import { NavigationStart, Router } from '@angular/router';
import { selectIsNavigationSubNavigationForcedToBeOpen } from 'ZalamoLibrary/src/lib/ui/navigation/navigation-state/navigation-state.selectors';
import { RxjsOperators } from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { translationProvider } from '@ui/nls/translations/translate.directive';

@Component({
    selector: 'lib-navigation-sub-menu',
    templateUrl: './navigation-sub-menu.component.html',
    styleUrls: ['./navigation-sub-menu.component.scss'],
    providers: [translationProvider('deliveryMethodEditor')],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NavigationSubMenuComponent
  extends AliveState
  implements AfterViewInit, OnInit, OnDestroy
{
  private readonly mainMotion = new MenuMotion();
  private readonly spacerMotion = new MenuMotion();
  private motionState = 'void';
  private motionElement?: HTMLElement;

  ngAfterViewChecked(): void {
    const element = this.main?.nativeElement;
    const spacer = this.spacer?.nativeElement;
    if (!element || !spacer) {
      this.mainMotion.destroy();
      this.spacerMotion.destroy();
      this.motionElement = undefined;
      return;
    }
    const state = this.openClose;
    if (this.motionState === state && this.motionElement === element) return;
    const previous = this.motionElement === element ? this.motionState : 'void';
    this.motionState = state;
    this.motionElement = element;
    const open = state === 'open' || state === 'open-hover';
    const width = open ? naturalMenuWidth(element) : '20px';
    const pinAfterClosing = previous === 'open' && state === 'closed';
    if (!pinAfterClosing) element.style.position = state === 'open' ? '' : 'fixed';
    // Outputs run after the current view check, as the former animation.start callback did.
    queueMicrotask(() => {
      if (this.motionElement === element && this.motionState === state) {
        this.openCloseEvent({ fromState: previous, toState: state });
      }
    });
    this.mainMotion.play(element, { width }, pinAfterClosing ? 400 : 100, pinAfterClosing);
    this.spacerMotion.play(spacer, { width: state === 'open' ? width : '20px' }, state === 'closed' ? 200 : 100);
  }

  @ViewChild('submenu', { static: false })
  submenu: ElementRef<HTMLElement> = null;
  @ViewChild('spacer', { static: false })
  spacer: ElementRef<HTMLElement> = null;
  @ViewChild('main', { static: false }) main: ElementRef<HTMLElement> = null;
  @ViewChild('mobileMenu', { static: false })
  mobileMenu: TemplateRef<HTMLElement> = null;
  @Output() menuClosed = new EventEmitter();
  @Input() showMobileMenu = new Subject<boolean>();
  hoverOpen$ = new BehaviorSubject(false);
  hoverOpen = false;
  mouseOverMenu = false;
  isMobile$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map(({ matches }) => matches),
    startWith(false),
    shareReplay(1)
  );
  ref: FullscreenRef<any> = null;
  @HostBinding('class.mayHaveCover') @Input() mayHaveCover = false;
  _mouseOverToggle = false;

  get mouseOverToggle() {
    return this._mouseOverToggle;
  }

  @HostBinding('class.hidden') get toggled() {
    return typeof localStorage.subNavToggled === 'undefined'
      ? false
      : localStorage.subNavToggled === 'true';
  }

  set toggled(toggled) {
    localStorage.subNavToggled = toggled;
  }

  get openClose() {
    let cState = null;
    if (this.toggled) {
      if (this.hoverOpen) {
        cState = 'open-hover';
      } else {
        // if (this.hoverOpen$.value !== this.hoverOpen) {
        //   cState = 'closed-hover';
        // } else {
        cState = 'closed';
        // }
      }
    } else {
      if (this.hoverOpen) {
        cState = 'open-hover';
      } else {
        cState = 'open';
      }
    }
    // const cState = !this.toggle ? 'open' : 'closed';
    return cState;
  }

  constructor(
    private elRef: ElementRef,
    private cd: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
    private dialog: ZalamoDialogService,
    private store: Store,
    private router: Router
  ) {
    super();
    if (this.breakpointObserver.isMatched(Breakpoints.HandsetLandscape)) {
      this.toggled = true;
    }
  }

  ngOnDestroy() {
    this.motionElement = undefined;
    this.mainMotion.destroy();
    this.spacerMotion.destroy();
    super.ngOnDestroy();
    this.store.dispatch(navigationSetHasSubmenuState({ hasSubmenu: false }));
  }

  setMouseOverToggle(setMouseOver, event: PointerEvent) {
    console.log(event);
    event.preventDefault();
    this._mouseOverToggle = setMouseOver;
    if (event.type === 'pointerleave') {
    }
  }

  openCloseEvent(event: { fromState: string; toState: string }) {
    if (event.fromState === 'open' && event.toState === 'closed') {
      this.menuClosed.emit(true);
    } else {
      if (event.toState === 'open') {
        this.menuClosed.emit(false);
      }
    }
  }

  onMouseEnter(event: PointerEvent) {
    this.mouseOverMenu = true;
    if (this.toggled) {
      this.hoverOpen$.next(true);
    }
  }

  onMouseLeave() {
    this.mouseOverMenu = false;
    if (this.toggled) {
      this.hoverOpen$.next(false);
    }
  }

  ngOnInit(): void {
    this.store.dispatch(navigationSetHasSubmenuState({ hasSubmenu: true }));

    this.subscribeWhileAlive(
      this.store.pipe(
        select(selectIsNavigationSubNavigationForcedToBeOpen),
        RxjsOperators.filterTruthy(),
        tap(() => this.hoverOpen$.next(true))
      ),
      this.hoverOpen$.pipe(
        debounceTime(200),
        filter(() => !this.mouseOverToggle),
        switchMap((hoverState) =>
          hoverState ? of(hoverState) : timer(200).pipe(mapTo(false))
        ),
        tap((x) => {
          this.hoverOpen = x;
          this.cd.markForCheck();
        })
      ),
      this.showMobileMenu.pipe(
        tap((show) => {
          console.log(show);
          if (show) {
            if (!this.ref) {
              this.ref = this.dialog.openFull(this.mobileMenu, {
                height: 'calc(100% - 50px)',
                closable: false,
                isBare: true,
              });
              this.ref
                .beforeClosed()
                .pipe(first())
                .subscribe(() => {
                  this.ref = null;
                  this.store.dispatch(navigationMobileSubmenuClose());
                });
              this.router.events
                .pipe(
                  takeUntil(this.ref.beforeClosed()),
                  tap((x) => console.log(x)),
                  filter((ev) => ev instanceof NavigationStart)
                )
                .subscribe(() => this.ref.close());
            } else {
              this.ref.close();
            }
          } else {
            if (this.ref) {
              this.ref.close();
            }
          }
        })
      )
    );
    this.menuClosed.emit(localStorage.subNavToggled === 'true');
    this.updateSpacerSize();
  }

  updateSpacerSize() {
    this.isMobile$.pipe(first()).subscribe((isMobile) => {
      if (!isMobile && !!this.spacer && !this.hoverOpen && !this.toggled) {
        const { width } = this.main.nativeElement.getBoundingClientRect();
        this.spacer.nativeElement.style.width = `${width}px`;
      }
    });
  }

  updateMenuMeasurement() {
    this.updateSpacerSize();

    this.isMobile$.pipe(first()).subscribe((isMobile) => {
      if (!isMobile && this.submenu && !this.hoverOpen) {
        const size = this.submenu.nativeElement.getBoundingClientRect();
        this.submenu.nativeElement.style.width = `${size.width}px`;
      }
    });
  }

  ngAfterViewInit() {
    this.updateMenuMeasurement();
    // const size2 = this.elRef.nativeElement.getBoundingClientRect();
    // this.elRef.nativeElement.style.width = `${size2.width}px`;
  }
}
